import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Check } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge, PageHeader } from "@/components/Badges";
import { KIND_LABELS, LOCKED_STATUSES } from "@/lib/format";
import JourneyPanel from "@/components/project/JourneyPanel";
import ProjectQuestionnaire from "@/components/project/ProjectQuestionnaire";
import DocumentsPanel from "@/components/project/DocumentsPanel";
import MissingAssistant from "@/components/project/MissingAssistant";
import IPPlan from "@/components/project/IPPlan";
import { MatchList } from "@/pages/app/Funding";

const TABS = [["grant-journey", "Grant journey"], ["patent-journey", "Patent journey"], ["details", "1. Describe"], ["ip", "2. IP options"], ["funding", "2. Funding options"], ["documents", "3. Documents"], ["assistant", "3. Missing information"], ["applications", "4–5. Applications"]];
const STAGES = ["Describe your project", "Check your options", "Prepare your information", "Review the application", "Complete and track"];
const AFTER = ["awaiting_review", "approved_for_submission", "ready_for_filing", ...LOCKED_STATUSES];

const Journey = ({ done }) => {
  const cur = done.findIndex((d) => !d);
  return (
  <ol className="grid grid-cols-5 gap-1" aria-label="Journey progress" data-testid="journey-stepper">
    {STAGES.map((l, i) => (
      <li key={l} className="text-center">
        <div className={`mx-auto grid h-8 w-8 place-items-center rounded-full border-2 text-xs font-bold ${done[i] ? "border-[#0D9488] bg-[#0D9488] text-white" : i === cur ? "border-[#0D9488] bg-teal-50 text-teal-800 ring-4 ring-teal-100" : "border-slate-300 bg-white text-slate-500"}`} data-testid={`journey-step-${i + 1}-${done[i] ? "done" : i === cur ? "current" : "todo"}`}>
          {done[i] ? <Check className="h-4 w-4" /> : i + 1}
        </div>
        <p className="mt-1 hidden text-[11px] text-slate-600 sm:block">{l}</p>
      </li>
    ))}
  </ol>
  );
};

const NEXT = [["details", "Describe the problem and your solution in plain English."], ["ip", "Check your options: choose what to protect, or review funding matches."], ["documents", "Upload supporting documents and fill any missing information."], ["applications", "Open an application and review it for approval."], ["applications", "Complete your application and track its progress."]];

const AppsTab = ({ project, apps }) => {
  const navigate = useNavigate();
  const create = async (kind) => {
    try {
      const { data } = await api.post(`/projects/${project.id}/applications`, { kind });
      navigate(`/app/applications/${data.id}`);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="space-y-6">
      <section className="card p-6">
        <h2 className="font-semibold text-[#0B192C]">Start an IP workflow</h2>
        <div className="mt-4 flex flex-wrap gap-2">
          {["patent", "trade_mark", "design", "copyright_record", "know_how"].map((k) => <button key={k} onClick={() => create(k)} data-testid={`create-app-${k}`} className="btn-ghost btn-sm">{KIND_LABELS[k]}</button>)}
        </div>
        <p className="mt-3 text-xs text-slate-500">For funding applications, open an opportunity from the Funding matches tab and choose “Prepare application”.</p>
      </section>
      <ul className="space-y-2">
        {apps.map((a) => (
          <li key={a.id}><Link to={`/app/applications/${a.id}`} className="card flex items-center justify-between p-4 hover:border-[#0D9488]"><span className="text-sm font-medium">{a.title}</span><StatusBadge status={a.status} kind={a.kind} /></Link></li>
        ))}
      </ul>
    </div>
  );
};

export default function ProjectDetail() {
  const { id } = useParams();
  const { orgId } = useAuth();
  const [project, setProject] = useState(null);
  const [docs, setDocs] = useState([]);
  const [missing, setMissing] = useState([]);
  const [apps, setApps] = useState([]);
  const [tab, setTab] = useState("grant-journey");
  const load = useCallback(async () => {
    const [p, d, m] = await Promise.all([api.get(`/projects/${id}`), api.get(`/projects/${id}/documents`), api.get(`/projects/${id}/missing`)]);
    setProject(p.data);
    setDocs(d.data);
    setMissing(m.data);
    const a = await api.get(`/orgs/${p.data.org_id}/applications`);
    setApps(a.data.filter((x) => x.project_id === id));
  }, [id]);
  useEffect(() => {
    load();
  }, [load]);
  if (!project) return <p className="text-sm text-slate-500">Loading…</p>;
  const done = [!!(project.problem && project.solution), (project.protect_choices || []).length > 0 || (!!project.ip_questions && Object.keys(project.ip_questions).length > 0),
    docs.length > 0 && missing.length === 0, apps.some((a) => AFTER.slice(1).includes(a.status)), apps.some((a) => LOCKED_STATUSES.includes(a.status))];
  return (
    <div className="space-y-6 fade-up">
      <Link to="/app/projects" className="text-sm text-[#0D9488] hover:underline">← Projects</Link>
      <PageHeader title={project.title} sub="Your answers save automatically. Facts from documents and AI suggestions are labelled separately from what you confirm." />
      <div className="card p-5"><Journey done={done} />
        {done.some((d) => !d) && (() => { const [t, txt] = NEXT[done.findIndex((d) => !d)]; return <p className="mt-4 text-sm text-slate-700" data-testid="project-next-action"><b>Next:</b> {txt} <button onClick={() => setTab(t)} className="font-semibold text-[#0D9488] underline" data-testid="project-next-button">Go</button></p>; })()}
      </div>
      <div className="flex gap-1 overflow-x-auto border-b border-slate-200" role="tablist">
        {TABS.map(([k, l]) => (
          <button key={k} role="tab" aria-selected={tab === k} onClick={() => { setTab(k); load(); }} data-testid={`project-tab-${k}`} className={`whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-medium ${tab === k ? "border-[#0D9488] text-[#0B192C]" : "border-transparent text-slate-500 hover:text-slate-800"}`}>{l}</button>
        ))}
      </div>
      {tab === "grant-journey" && <JourneyPanel key="grant" projectId={id} kind="grant" goTab={setTab} />}
      {tab === "patent-journey" && <JourneyPanel key="patent" projectId={id} kind="patent" goTab={setTab} />}
      {tab === "details" && <ProjectQuestionnaire project={project} onSaved={setProject} />}
      {tab === "documents" && <DocumentsPanel project={project} docs={docs} reload={load} />}
      {tab === "ip" && <IPPlan project={project} onSaved={setProject} goDetails={() => setTab("details")} />}
      {tab === "funding" && <MatchList orgId={orgId} projectId={id} compact />}
      {tab === "assistant" && <MissingAssistant project={project} missing={missing} reload={load} />}
      {tab === "applications" && <AppsTab project={project} apps={apps} />}
    </div>
  );
}
